import React from 'react';
import { Eye, Clock, TrendingUp } from 'lucide-react';

function PageAnalytics({ data }) {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="text-center py-8 text-gray-500">No page analytics data available</div>;
  }

  // Calculate summary statistics
  const totalPageViews = data.reduce((sum, page) => sum + page.count, 0);
  const totalUniqueVisitors = data.reduce((sum, page) => sum + page.uniqueVisitors, 0);
  const averageTimeOnPage =
    data.reduce((sum, page) => sum + page.averageTimeOnPage, 0) / data.length;
  const mostPopularPage = data[0];

  // Format time duration
  const formatDuration = milliseconds => {
    if (!milliseconds || milliseconds === 0) return '0s';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Format page URL for display
  const formatPageUrl = url => {
    if (!url || url === '/') return 'Homepage';

    // Remove query parameters and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // If it's just a slash, return Homepage
    if (cleanUrl === '/') return 'Homepage';

    // Remove leading slash and capitalize
    const segments = cleanUrl.substring(1).split('/');
    return segments.map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join(' / ');
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-600">Total Page Views</p>
              <p className="text-2xl font-bold text-blue-900">{totalPageViews.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-600">Unique Visitors</p>
              <p className="text-2xl font-bold text-green-900">
                {totalUniqueVisitors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-600">Avg. Time on Page</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatDuration(averageTimeOnPage)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-orange-600">Most Popular</p>
              <p
                className="text-sm font-bold text-orange-900 truncate"
                title={formatPageUrl(mostPopularPage.page)}
              >
                {formatPageUrl(mostPopularPage.page)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Most Popular Page Highlight */}
      {mostPopularPage && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
            Most Popular Page
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Page</p>
              <p className="font-mono text-sm bg-white p-2 rounded border break-all">
                {mostPopularPage.page}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Page Views</p>
              <p className="text-2xl font-bold text-purple-600">
                {mostPopularPage.count.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Unique Visitors</p>
              <p className="text-2xl font-bold text-blue-600">
                {mostPopularPage.uniqueVisitors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pages Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Page Performance</h3>
          <p className="text-sm text-gray-600">Detailed analytics for each page</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Visitors
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg. Time on Page
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Popularity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((page, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-purple-600">{index + 1}</span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatPageUrl(page.page)}
                        </div>
                        <div className="text-sm text-gray-500 font-mono truncate max-w-xs">
                          {page.page}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {page.count.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {page.uniqueVisitors.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDuration(page.averageTimeOnPage)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                        <div
                          className="bg-purple-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(100, (page.count / data[0].count) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm text-gray-500">
                        {Math.round((page.count / data[0].count) * 100)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Page Categories Analysis */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Page Categories</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {['/', '/admin', '/reports', '/login', '/register'].map(category => {
            const categoryPages = data.filter(
              page => page.page.startsWith(category) || (category === '/' && page.page === '/'),
            );

            if (categoryPages.length === 0) return null;

            const totalViews = categoryPages.reduce((sum, page) => sum + page.count, 0);
            const categoryLabels = {
              '/': 'Homepage',
              '/admin': 'Admin Pages',
              '/reports': 'Reports',
              '/login': 'Authentication',
              '/register': 'Authentication',
            };
            const categoryName = categoryLabels[category] ?? 'Other Pages';

            return (
              <div key={category} className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{categoryName}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Views</span>
                    <span className="font-medium">{totalViews.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Pages</span>
                    <span className="font-medium">{categoryPages.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Avg. Views/Page</span>
                    <span className="font-medium">
                      {Math.round(totalViews / categoryPages.length).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PageAnalytics;
